import { useIsMobile } from "@justhome/common/hooks";
import { Button } from "@justhome/react-ui";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "../../lib/hooks/useTranslations";
import DynamicFormField from "./DynamicFormField";
import Submit from "./Submit";
import { FieldValues, FormCondition, FormDefinition } from "./interfaces";

export interface DynamicFormProps<T extends FieldValues> {
  formDefinition: FormDefinition<T>;
  onSubmit: (value: any, form: any) => void;
  defaultValues: DefaultValues<T>;
  id?: string;
  onPrevious?: VoidFunction;
}

export const getFilteredListByCondition = <
  T extends { condition?: FormCondition<any> },
  S extends FieldValues,
>(
  data?: T[],
  defaultValues?: S,
): T[] => {
  return (
    data?.filter((item: T) =>
      item.condition ? item.condition(defaultValues) : true,
    ) || []
  );
};

const DynamicForm = <T extends FieldValues>({
  formDefinition,
  onSubmit,
  defaultValues,
  id,
  onPrevious,
}: DynamicFormProps<T>) => {
  const form = useForm<T>({ mode: "onTouched", defaultValues });
  const handleSubmit = form.handleSubmit(onSubmit);
  const values = form.watch();
  const fields = getFilteredListByCondition(formDefinition.fields, values);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <form noValidate className="space-y-3s" onSubmit={handleSubmit} id={id}>
      {fields.map((field, index) => (
        <DynamicFormField
          key={index}
          onSubmit={handleSubmit}
          fieldDefinition={field}
          control={form.control}
          autoFocus={!isMobile && index === 0}
        />
      ))}
      <div className="items-center flex justify-between s:justify-start">
        {onPrevious && (
          <Button
            onClick={onPrevious}
            className="mr-m"
            variant="ghost"
            paddingX={false}
            size="md"
          >
            {t("link_back_previous")}
          </Button>
        )}
        {formDefinition.submit && <Submit>{t("form_button_next")}</Submit>}
      </div>
    </form>
  );
};

export default DynamicForm;
export * from "./interfaces";
